import { t, Instance } from 'mobx-state-tree';
import type { FactTypeResponse } from 'we-tree-api-client/v2';

/**
 * FactTypeModel - Represents a fact type from the database
 */
export const FactTypeModel = t
  .model('FactTypeModel', {
    id: t.string,
    slug: t.string,
    displayName: t.string,
    description: t.maybeNull(t.string),
    category: t.maybeNull(t.string),
    gedcomTag: t.maybeNull(t.string),
    useCustomGedcomTag: t.optional(t.boolean, false),
    isSystem: t.optional(t.boolean, false),
    isActive: t.optional(t.boolean, true),
    createdAt: t.string,
    updatedAt: t.string,
    createdBy: t.maybeNull(t.string),
    updatedBy: t.maybeNull(t.string)
  })
  .views(() => ({
    // Add any computed views if needed
  }));

export type FactTypeType = Instance<typeof FactTypeModel>;

/**
 * Helper function to create FactTypeModel from API response
 * API now returns data in the correct format, so minimal transformation is needed
 */
export function createFactTypeModelFromResponse(response: FactTypeResponse): Instance<typeof FactTypeModel> {
  // Simple boolean coercion in case MySQL returns 0/1 instead of true/false
  const coerceBoolean = (value: unknown, defaultValue: boolean): boolean => {
    if (typeof value === 'boolean') return value;
    if (value === 1 || value === '1' || value === true) return true;
    if (value === 0 || value === '0' || value === false) return false;
    return defaultValue;
  };

  return FactTypeModel.create({
    id: response.id,
    slug: response.slug,
    displayName: response.displayName,
    description: response.description ?? null,
    category: response.category ?? null,
    gedcomTag: response.gedcomTag ?? null,
    useCustomGedcomTag: coerceBoolean(response.useCustomGedcomTag, false),
    isSystem: coerceBoolean(response.isSystem, false),
    isActive: coerceBoolean(response.isActive, true),
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
    createdBy: response.createdBy ?? null,
    updatedBy: response.updatedBy ?? null
  });
}
